import { AxiosError } from 'axios';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  AutomationError,
  CreateAutomationPayload,
  SalesforceActions,
  SalesforceAPI,
  SalesforceAutomation,
  SalesforceConfig,
  SalesforceFieldMapping,
  SalesforceIntegrationProvider,
  SalesforceObject,
  SalesforceOrderPayload,
  SalesforceResourceCreatePayload,
  SalesforceResourceMapping,
  SfInstallations,
  UpdateAutomationPayload,
} from 'api/integrations';
import { IntegrationField } from 'api/integrations/types';

export const SALESFORCE_CONFIG_QUERY_KEY = 'getSalesforceConfig';

export const getSfInstallationsQueryKey = (intergationId: number): string =>
  `sf-installations-${intergationId}`;

export const useSalesforceConfigQuery = (
  options: UseQueryOptions<
    SalesforceConfig[],
    unknown,
    SalesforceConfig[],
    typeof SALESFORCE_CONFIG_QUERY_KEY
  > = {},
): UseQueryResult<SalesforceConfig[], unknown> => {
  return useQuery(
    SALESFORCE_CONFIG_QUERY_KEY,
    () => SalesforceAPI.getIntegrationConfig(),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
};

export const useSalesforceConfigByIdQuery = (
  integrationId: number,
  options: UseQueryOptions<
    SalesforceConfig,
    unknown,
    SalesforceConfig,
    [typeof SALESFORCE_CONFIG_QUERY_KEY, number]
  > = {},
): UseQueryResult<SalesforceConfig, unknown> => {
  return useQuery(
    [SALESFORCE_CONFIG_QUERY_KEY, integrationId],
    () => SalesforceAPI.getIntegrationConfigById(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 0,
      cacheTime: 0,
      ...options,
    },
  );
};

export const useDisconnectSalesForceMutation = (): UseMutationResult<
  void,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(
    (integrationId) => {
      return SalesforceAPI.disconnectIntegration(integrationId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(SALESFORCE_CONFIG_QUERY_KEY);
      },
    },
  );
};

export const SALESFORCE_AUTH_QUERY_KEY = 'salesforceAuthenticationDetails';

export const useSalesForceAuthenticateQuery = (
  options: UseQueryOptions<
    SalesforceIntegrationProvider,
    unknown,
    SalesforceIntegrationProvider,
    [typeof SALESFORCE_AUTH_QUERY_KEY]
  > = {},
): UseQueryResult<SalesforceIntegrationProvider, unknown> => {
  return useQuery(
    [SALESFORCE_AUTH_QUERY_KEY],
    () => SalesforceAPI.getAuthDetails({ params: {} }),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const SALESFORCE_TRIGGER_OBJECTS_QUERY_KEY = 'salesforceTriggerObjects';

export const useSalesForceTriggerObjectsQuery = (
  integrationId: number,
  options: UseQueryOptions<
    SalesforceObject[],
    unknown,
    SalesforceObject[],
    [typeof SALESFORCE_TRIGGER_OBJECTS_QUERY_KEY, number]
  > = {},
): UseQueryResult<SalesforceObject[], unknown> => {
  return useQuery(
    [SALESFORCE_TRIGGER_OBJECTS_QUERY_KEY, integrationId],
    () => SalesforceAPI.getTriggerObjects(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      ...options,
    },
  );
};

export const SALESFORCE_AUTOMATION_TRIGGER_FIELDS_QUERY_KEY =
  'salesforceAutomationTriggerFields';

export const useSalesForceTriggerFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof SALESFORCE_AUTOMATION_TRIGGER_FIELDS_QUERY_KEY, number, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery(
    [
      SALESFORCE_AUTOMATION_TRIGGER_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
    ],
    () =>
      SalesforceAPI.getAutomationTriggerFields(integrationId, salesforceObject),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      ...options,
    },
  );
};

export const SALESFORCE_AUTOMATION_MATCHES_FIELDS_QUERY_KEY =
  'salesforceAutomationMatchFields';

export const useSalesForceMatchFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof SALESFORCE_AUTOMATION_MATCHES_FIELDS_QUERY_KEY, number, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery(
    [
      SALESFORCE_AUTOMATION_MATCHES_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
    ],
    () =>
      SalesforceAPI.getAutomationMatchFields(integrationId, salesforceObject),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      ...options,
    },
  );
};

export const SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY =
  'salesforceAutomationActionFields';

export const useSalesForceActionFieldsQuery = (
  integrationId: number,
  salesforceObject: string,
  actionType: SalesforceActions,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY, number, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery(
    [
      SALESFORCE_AUTOMATION_ACTION_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
    ],
    () =>
      SalesforceAPI.getSalesforceActionFields(
        integrationId,
        salesforceObject,
        actionType,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      ...options,
    },
  );
};

export const SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY =
  'salesforceAutomationTemplatePlaceholdersFields';

export const useTemplatePlaceholders = (
  integrationId: number,
  salesforceObject: string,
  templateId: any,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [
      typeof SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
      number,
      string,
      string,
    ]
  > = {},
): UseQueryResult<IntegrationField[], unknown> => {
  return useQuery(
    [
      SALESFORCE_AUTOMATION_TEMPLATE_PLACEHOLDERS_FIELDS_QUERY_KEY,
      integrationId,
      salesforceObject,
      templateId,
    ],
    () =>
      SalesforceAPI.getTemplatePlaceholders(
        integrationId.toString(),
        templateId,
        salesforceObject,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const AUTOMATIONS_LIST_QUERY_KEY = 'salesforceAutomationsList';

export const useSalesForceAutomationsList = (
  options: UseQueryOptions<
    SalesforceAutomation[],
    unknown,
    SalesforceAutomation[],
    typeof AUTOMATIONS_LIST_QUERY_KEY
  > = {},
): UseQueryResult<SalesforceAutomation[], unknown> => {
  return useQuery(
    AUTOMATIONS_LIST_QUERY_KEY,
    () => SalesforceAPI.getAutomations(),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const AUTOMATIONS_DETAILS_QUERY_KEY = 'salesforceAutomationsDetails';

export const useSalesForceAutomationsDetails = (
  automationId: number,
  options: UseQueryOptions<
    SalesforceAutomation,
    unknown,
    SalesforceAutomation,
    [typeof AUTOMATIONS_DETAILS_QUERY_KEY, number]
  > = {},
): UseQueryResult<SalesforceAutomation, unknown> => {
  return useQuery(
    [AUTOMATIONS_DETAILS_QUERY_KEY, automationId],
    () => SalesforceAPI.getAutomationById(automationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
};

export const useCreateAutomationMutation = (): UseMutationResult<
  SalesforceAutomation,
  AxiosError<{ errors: AutomationError[] }>,
  CreateAutomationPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    SalesforceAutomation,
    AxiosError<{ errors: AutomationError[] }>,
    CreateAutomationPayload
  >(
    (payload: CreateAutomationPayload) => {
      return SalesforceAPI.createAutomation(payload);
    },
    {
      onSuccess: async () => {
        await queryClient.refetchQueries(AUTOMATIONS_LIST_QUERY_KEY);
      },
    },
  );
};

export const useUpdateAutomationMutation = (): UseMutationResult<
  SalesforceAutomation,
  AxiosError<{ errors: AutomationError[] }>,
  UpdateAutomationPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    SalesforceAutomation,
    AxiosError<{ errors: AutomationError[] }>,
    UpdateAutomationPayload
  >(
    (payload: UpdateAutomationPayload) => {
      return SalesforceAPI.updateAutomation(payload);
    },
    {
      onSuccess: async () => {
        queryClient.refetchQueries(AUTOMATIONS_DETAILS_QUERY_KEY);
      },
    },
  );
};

export const useDeleteAutomationMutation = (): UseMutationResult<
  void,
  unknown,
  number
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(
    (automationId: number) => {
      return SalesforceAPI.deleteAutomation(automationId);
    },
    {
      onSuccess: async () => {
        queryClient.refetchQueries(AUTOMATIONS_DETAILS_QUERY_KEY);
      },
    },
  );
};

export const useReorderAutomationMutation = (): UseMutationResult<
  SalesforceAutomation[],
  unknown,
  SalesforceOrderPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<SalesforceAutomation[], unknown, SalesforceOrderPayload>(
    ({ automationId, automationUpdatedOrder }) => {
      return SalesforceAPI.reorderAutomation(
        automationId,
        automationUpdatedOrder,
      );
    },
    {
      onSuccess: async (resp) => {
        await queryClient.setQueryData(AUTOMATIONS_LIST_QUERY_KEY, resp);
      },
    },
  );
};

export const SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY =
  'salesforceResourceMappings';

export const useSalesForceResourceMappings = (
  integrationId: number,
  options: UseQueryOptions<
    SalesforceResourceMapping[],
    unknown,
    SalesforceResourceMapping[],
    [typeof SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY, number]
  > = {},
): UseQueryResult<SalesforceResourceMapping[], unknown> =>
  useQuery(
    [SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY, integrationId],
    () => SalesforceAPI.getResourceMappings(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_EXTERNAL_RESOURCE_TYPES_QUERY_KEY =
  'salesforceExternalResourceTypes';

export const useSalesForceExternalResourceTypes = (
  integrationId: number,
  options: UseQueryOptions<
    SalesforceObject[],
    unknown,
    SalesforceObject[],
    [typeof SALESFORCE_EXTERNAL_RESOURCE_TYPES_QUERY_KEY, number]
  > = {},
): UseQueryResult<SalesforceObject[], unknown> =>
  useQuery(
    [SALESFORCE_EXTERNAL_RESOURCE_TYPES_QUERY_KEY, integrationId],
    () => SalesforceAPI.getExternalResourceTypes(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_LINK_OBJECT_KEY = 'salesforceLinkObjectKey';

export const useSalesForceLinkObjectQuery = (
  integrationId: number,
  projectId: number,
  salesforceObjectUrl: string,
  options: UseQueryOptions<
    SalesforceObject,
    unknown,
    SalesforceObject,
    [typeof SALESFORCE_LINK_OBJECT_KEY, number]
  > = {},
) =>
  useQuery(
    [SALESFORCE_LINK_OBJECT_KEY, integrationId],
    () =>
      SalesforceAPI.linkSalesforceObject(integrationId, {
        projectId,
        sObjectUrl: salesforceObjectUrl,
      }),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );
export const SALESFORCE_UN_LINK_OBJECT_KEY = 'salesforceUnLinkObjectKey';

export const useSalesForceUnLinkObjectQuery = (
  integrationId: number,
  resourceMappingId: number,
  options: UseQueryOptions<
    SalesforceObject,
    unknown,
    SalesforceObject,
    [typeof SALESFORCE_LINK_OBJECT_KEY, number]
  > = {},
) =>
  useQuery(
    [SALESFORCE_LINK_OBJECT_KEY, integrationId],
    () =>
      SalesforceAPI.unLinkSalesforceObjectWithProject(
        integrationId,
        resourceMappingId,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_GET_LINK_OBJECT_KEY = 'salesforceGetLinkObjectKey';

export const useSalesForceGetLinkObjectQuery = (
  integrationId?: number,
  externalResourceId?: string,
  externalResourceType?: string,
  options: UseQueryOptions<
    SalesforceObject,
    unknown,
    SalesforceObject,
    typeof SALESFORCE_GET_LINK_OBJECT_KEY
  > = {},
) =>
  useQuery(
    SALESFORCE_GET_LINK_OBJECT_KEY,
    () =>
      SalesforceAPI.getSalesforceObjectWithProject(
        integrationId,
        externalResourceId,
        externalResourceType,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_NATIVE_RESOURCE_TYPES_QUERY_KEY =
  'salesforceNativeResourceTypes';

export const useSalesForceNativeResourceTypes = (
  integrationId: number,
  options: UseQueryOptions<
    SalesforceResourceMapping[],
    unknown,
    SalesforceResourceMapping[],
    [typeof SALESFORCE_NATIVE_RESOURCE_TYPES_QUERY_KEY, number]
  > = {},
): UseQueryResult<SalesforceResourceMapping[], unknown> =>
  useQuery(
    [SALESFORCE_NATIVE_RESOURCE_TYPES_QUERY_KEY, integrationId],
    () => SalesforceAPI.getNativeResourceTypes(integrationId),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY =
  'salesforceExternalResourceFields';

export const useSalesForceExternalResourceFields = (
  integrationId: number,
  externalResourceType: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof SALESFORCE_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY, number, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery(
    [
      SALESFORCE_EXTERNAL_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      externalResourceType,
    ],
    () =>
      SalesforceAPI.getExternalResourceFields(
        integrationId,
        externalResourceType,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_NATIVE_RESOURCE_FIELDS_QUERY_KEY =
  'salesforceNativeResourceFields';

export const useSalesForceNativeResourceFields = (
  integrationId: number,
  nativeResourceType: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [typeof SALESFORCE_NATIVE_RESOURCE_FIELDS_QUERY_KEY, number, string]
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery(
    [
      SALESFORCE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      nativeResourceType,
    ],
    () =>
      SalesforceAPI.getNativeResourceFields(integrationId, nativeResourceType),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const SALESFORCE_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY =
  'salesforceMappableNativeResourceFields';

export const useSalesForceMappableNativeResourceFields = (
  integrationId: number,
  nativeResourceType: string,
  externalResourceType: string,
  externalResourceFieldId: string,
  options: UseQueryOptions<
    IntegrationField[],
    unknown,
    IntegrationField[],
    [
      typeof SALESFORCE_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      number,
      string,
      string,
      string,
    ]
  > = {},
): UseQueryResult<IntegrationField[], unknown> =>
  useQuery(
    [
      SALESFORCE_MAPPABLE_NATIVE_RESOURCE_FIELDS_QUERY_KEY,
      integrationId,
      nativeResourceType,
      externalResourceType,
      externalResourceFieldId,
    ],
    () =>
      SalesforceAPI.getMappableNativeResourceFields(
        integrationId,
        nativeResourceType,
        externalResourceType,
        externalResourceFieldId,
      ),
    {
      retry: 1,
      refetchOnWindowFocus: false,
      ...options,
    },
  );

export const useSalesForceCreateResourceMutation = (
  integrationId: number,
): UseMutationResult<
  SalesforceResourceMapping,
  unknown,
  SalesforceResourceCreatePayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    SalesforceResourceMapping,
    unknown,
    SalesforceResourceCreatePayload
  >(
    (payload) => {
      return SalesforceAPI.createResourceObject(integrationId, payload);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const useSalesForceDeleteResourceMutation = (
  integrationId: number,
): UseMutationResult<void, unknown, number> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, number>(
    (resourceId: number) => {
      return SalesforceAPI.deleteResourceObject(integrationId, resourceId);
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const useSalesForceDeleteMappingMutation = (
  integrationId: number,
): UseMutationResult<
  void,
  unknown,
  { resourceId: number; mappingId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation<void, unknown, { resourceId: number; mappingId: number }>(
    (payload: { resourceId: number; mappingId: number }) => {
      return SalesforceAPI.deleteFieldMapping(
        integrationId,
        payload.resourceId,
        payload.mappingId,
      );
    },
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

type CreatePayload = {
  resourceId: number;
  mappingData: SalesforceFieldMapping;
};

export const useSalesforceMappingCreateMutation = (
  integrationId: number,
): UseMutationResult<SalesforceFieldMapping, unknown, CreatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<SalesforceFieldMapping, unknown, CreatePayload>(
    (payload: CreatePayload) =>
      SalesforceAPI.createFieldMapping(
        integrationId,
        payload.resourceId,
        payload.mappingData,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

type UpdatePayload = {
  resourceId: number;
  fieldMappingId: number;
  mappingData: SalesforceFieldMapping;
};

export const useSalesforceMappingUpdateMutation = (
  integrationId: number,
): UseMutationResult<SalesforceFieldMapping, unknown, UpdatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<SalesforceFieldMapping, unknown, UpdatePayload>(
    (payload: UpdatePayload) =>
      SalesforceAPI.updateFieldMapping(
        integrationId,
        payload.resourceId,
        payload.fieldMappingId,
        payload.mappingData,
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          SALESFORCE_RESOURCE_MAPPINGS_QUERY_KEY,
        );
      },
    },
  );
};

export const useGetSfInstallationsQuery = (
  integrationId: number,
  enabled: boolean,
): UseQueryResult<SfInstallations[]> =>
  useQuery(
    getSfInstallationsQueryKey(integrationId),
    () => SalesforceAPI.getSalesForceInstallations(integrationId),
    {
      enabled,
    },
  );
